import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { RiskType } from '@/models';
import { TransactionAlertsLevels } from '@/api';
import { RiskKeys } from '@/config';

import { TransactionAlertsTableGroup } from '../TransactionAlertsTableGroup/TransactionAlertsTableGroup';
import { TransactionAlertsTableHeader } from '../TransactionAlertsTableHeader/TransactionAlertsTableHeader';
import { TransactionAlertsTableRow } from '../TransactionAlertsTableRow/TransactionAlertsTableRow';
import { TransactionAlertsEditValue } from '../TransactionAlertsEditModal/TransactionAlertsEditModal.constants';
import {
  transactionAlertsTableGroups,
  DEFAULT_EXPANDED_STATE,
} from './TransactionAlertsTable.constants';
import { getGroupLevels } from './TransactionAlertsTable.helpers';
import styles from './TransactionAlertsTable.module.sass';

interface TransactionAlertsTableInterface {
  data: TransactionAlertsLevels;
  isScrolled?: boolean;
  isEditable?: boolean;
  onEdit?: (col: TransactionAlertsEditValue) => void;
  onClearCategory?: (signal: RiskKeys) => void;
}

export const TransactionAlertsTable = ({
  data,
  isScrolled = false,
  isEditable = false,
  onEdit = noop,
  onClearCategory = noop,
}: TransactionAlertsTableInterface) => {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState(DEFAULT_EXPANDED_STATE);

  const handleOnEdit = useCallback(
    (col: TransactionAlertsEditValue) => {
      if (!isEditable) return;
      onEdit(col);
    },
    [onEdit],
  );

  const handleOnToggleGroup = (group: RiskType) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  return (
    <div className={styles.table}>
      <TransactionAlertsTableHeader isScrolled={isScrolled} />
      {transactionAlertsTableGroups.map((group) => {
        const groupLevels = getGroupLevels(group, data);
        const titleMap: Record<RiskType, string> = {
          trusted: t('translation.trusted_sources'),
          suspicious: t('translation.suspicious_sources'),
          danger: t('translation.danger'),
        };

        return (
          <TransactionAlertsTableGroup
            key={group.title}
            isExpanded={expandedGroups[group.type]}
            title={titleMap[group.type]}
            firstText={`${t('translation.min_amount')}, USD`}
            secondText={`${t('translation.direct')} / ${t(
              'translation.indirect',
            )}`}
            onClick={() => handleOnToggleGroup(group.type)}
          >
            {groupLevels.map((level) => (
              <TransactionAlertsTableRow
                key={`${level.category}-${level.direction}`}
                isEditable={isEditable}
                isClearable={isEditable}
                category={level.category}
                direction={level.direction}
                low={{
                  direct: level.low.direct.value ?? null,
                  indirect: level.low.indirect.value ?? null,
                }}
                medium={{
                  direct: level.medium.direct.value ?? null,
                  indirect: level.medium.indirect.value ?? null,
                }}
                high={{
                  direct: level.high.direct.value ?? null,
                  indirect: level.high.indirect.value ?? null,
                }}
                severe={{
                  direct: level.severe.direct.value ?? null,
                  indirect: level.severe.indirect.value ?? null,
                }}
                onClearCategory={() => onClearCategory(level.category)}
                onEdit={handleOnEdit}
              />
            ))}
          </TransactionAlertsTableGroup>
        );
      })}
    </div>
  );
};
